export const APP_ERROR_DIGEST_TIMEOUT = "APP_TIMEOUT";
export const APP_ERROR_DIGEST_NETWORK = "APP_NETWORK";
// Upstream/origin failure surfaced as a 5xx (e.g. Cloudflare 522, Django 500).
export const APP_ERROR_DIGEST_SERVER = "APP_SERVER";
export const CHUNK_RELOAD_SESSION_KEY = "chunkReloadAttempted";

type ErrorLike = {
  isTimeout?: unknown;
  isNetwork?: unknown;
  isServerError?: unknown;
  status?: unknown;
  name?: unknown;
  code?: unknown;
  message?: unknown;
  digest?: unknown;
};

const isServerErrorStatus = (status: unknown): boolean =>
  typeof status === "number" && status >= 500 && status <= 599;

// Matches "HTTP 522", "HTTP Error! Status: 500", "status 503" etc. — the
// shapes our thrown errors and Cloudflare/Django pages produce.
const mentionsServerErrorStatus = (message: string): boolean =>
  /\bhttp\b[^\d]{0,12}5\d\d/.test(message) ||
  /\bstatus\b[^\d]{0,6}5\d\d/.test(message);

const hasDigestMarker = (digest: unknown, marker: string): boolean => {
  if (typeof digest !== "string") return false;
  return digest.toUpperCase().includes(marker);
};

export const classifyRuntimeError = (
  error: unknown,
): {
  isTimeout: boolean;
  isNetwork: boolean;
  isServerError: boolean;
  isChunkLoad: boolean;
} => {
  if (!error || typeof error !== "object") {
    const message = String(error ?? "").toLowerCase();
    return {
      isTimeout: /timeout/.test(message),
      isNetwork: /network/.test(message),
      isServerError: mentionsServerErrorStatus(message),
      isChunkLoad:
        /loading( css)? chunk \d+ failed/i.test(message) ||
        /failed to fetch dynamically imported module/i.test(message),
    };
  }

  const err = error as ErrorLike;
  const name = String(err.name ?? "").toLowerCase();
  const code = String(err.code ?? "").toLowerCase();
  const message = String(err.message ?? "").toLowerCase();

  const isTimeout =
    Boolean(err.isTimeout) ||
    name === "timeouterror" ||
    code === "etimedout" ||
    hasDigestMarker(err.digest, APP_ERROR_DIGEST_TIMEOUT) ||
    /timeout/.test(message);

  const isNetwork =
    Boolean(err.isNetwork) ||
    name === "networkerror" ||
    code === "enetwork" ||
    hasDigestMarker(err.digest, APP_ERROR_DIGEST_NETWORK) ||
    /network/.test(message);

  const isServerError =
    Boolean(err.isServerError) ||
    isServerErrorStatus(err.status) ||
    hasDigestMarker(err.digest, APP_ERROR_DIGEST_SERVER) ||
    mentionsServerErrorStatus(message);

  const isChunkLoad =
    name === "chunkloaderror" ||
    /loading( css)? chunk \d+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message);

  return { isTimeout, isNetwork, isServerError, isChunkLoad };
};
